/** @format */

import PlatformMsisdnPoolNumber from '../../interfaces/platform/PlatformMsisdnPoolNumber';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { IndicatedRangeLoader } from '../indicated-range/IndicatedRangeLoader';
import { PlatformResellerLoader } from '../platformreseller/PlatformResellerLoader';
import Order from '../../interfaces/forms/Order';
import Filter from '../../interfaces/forms/Filter';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import { PlatformMsisdnPoolNumberCollectionInterface } from '../../interfaces/collections/PlatformMsisdnPoolNumberCollectionInterface';
import { IccidMvnoStatusHelper } from '../../views/lib/search/IccidMvnoStatusHelper';
import moment from 'moment';
import i18next from 'i18next';
import { ExportToCsv } from 'export-to-csv';
import { msisdnStatus } from '../../helpers/msisdnStatus';
import { DateHelperTs } from '../../helpers/DateHelperTs';

const _ = require('underscore');

interface StorageMsisdnToCsvPartner {
    msisdn: string;
    numberState: string;
    activationCount: number;
    portinDate: string | null;
    // indicatedRangeName: string | null;
}

interface StorageMsisdnToCsvSuperUser extends StorageMsisdnToCsvPartner {
    partnerName: string | null;
    sharedDate: string | null;
    sharedEndDate: string | null;
}

export class StorageMsisdnToCsv {
    private readonly itemLoader: ItemLoaders;
    private readonly indicatedRangeLoader: IndicatedRangeLoader;
    private readonly platformResellerLoader: PlatformResellerLoader;
    private readonly iccidStatusHelper: IccidMvnoStatusHelper;

    constructor(
        itemLoader: ItemLoaders,
        indicatedRangeLoader: IndicatedRangeLoader,
        platformResellerLoader: PlatformResellerLoader,
        iccidStatusHelper: IccidMvnoStatusHelper
    ) {
        this.itemLoader = itemLoader;
        this.indicatedRangeLoader = indicatedRangeLoader;
        this.platformResellerLoader = platformResellerLoader;
        this.iccidStatusHelper = iccidStatusHelper;
    }

    exportToCsv = (isSuperUser: boolean, orders: Array<Order>, filters: Array<Filter>, showMainLoader: (show: boolean) => void): void => {
        const extendFilter = _.clone(filters);
        extendFilter.push({ name: 'pagination', value: 'false' });
        showMainLoader(true);
        this.itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/platform_msisdn_pool_numbers')),
            'storage_msisdn_loader_items',
            extendFilter,
            orders,
            (items: PlatformMsisdnPoolNumberCollectionInterface): void => {
                if (isSuperUser) {
                    this.platformResellerLoader.loadPlatformResellers({
                        filters: [],
                        callback: (): void => {
                            this.prepareSuperUserCsv(items);
                        },
                    });
                } else {
                    this.preparePartnerUserCsv(items);
                }
                showMainLoader(false);
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    prepareSuperUserCsv = (items: PlatformMsisdnPoolNumberCollectionInterface): void => {
        const csvList = this.prepareCsvData(items);
        const options = {
            filename: 'msisdnPoolsReport' + moment().format('YYYYMMDD[_]HHmm'),
            fieldSeparator: ';',
            quoteStrings: '"',
            decimalSeparator: '.',
            showLabels: true,
            showTitle: false,
            title: 'PliCbd2 report',
            useTextFile: false,
            useBom: true,
            useKeysAsHeaders: false,
            headers: [
                i18next.t('simcard:list.msisdn'),
                i18next.t('prov:list.partner'),
                i18next.t('pools:msisdn.number_state'),
                i18next.t('simcard:list.share_date'),
                i18next.t('simcard:list.share_end'),
                i18next.t('simcard:list.activate_count'),
                i18next.t('simcard:list.portin_date'),
                // i18next.t('simcard:list.indicated_range'),
            ],
        };
        const exportCsv = new ExportToCsv(options);
        exportCsv.generateCsv(csvList);
    };

    preparePartnerUserCsv = (items: PlatformMsisdnPoolNumberCollectionInterface): void => {
        const csvList = this.prepareCsvData(items);
        const options = {
            filename: 'msisdnPoolsReport' + moment().format('YYYYMMDD[_]HHmm'),
            fieldSeparator: ';',
            quoteStrings: '"',
            decimalSeparator: '.',
            showLabels: true,
            showTitle: false,
            title: 'PliCbd2 report',
            useTextFile: false,
            useBom: true,
            useKeysAsHeaders: false,
            headers: [
                i18next.t('simcard:list.msisdn'),
                i18next.t('pools:msisdn.number_state'),
                i18next.t('simcard:list.activate_count'),
                i18next.t('simcard:list.portin_date'),
                // i18next.t('simcard:list.indicated_range'),
            ],
        };
        const csvPartnerList: Array<StorageMsisdnToCsvPartner> = [];
        csvList.map((item) => {
            csvPartnerList.push({
                msisdn: item.msisdn,
                numberState: item.numberState,
                activationCount: item.activationCount,
                portinDate: item.portinDate ? DateHelperTs.format(item.portinDate, 'YYYY-MM-DD') : '',
                // indicatedRangeName: item.indicatedRangeName,
            });
        });
        const exportCsv = new ExportToCsv(options);
        exportCsv.generateCsv(csvPartnerList);
    };

    prepareCsvData = (items: PlatformMsisdnPoolNumberCollectionInterface): Array<StorageMsisdnToCsvSuperUser> => {
        const csvList: Array<StorageMsisdnToCsvSuperUser> = [];
        if (items['hydra:member'] && items['hydra:totalItems'] && items['hydra:totalItems'] > 0) {
            items['hydra:member'].map((item: PlatformMsisdnPoolNumber) => {
                const partner = this.platformResellerLoader.getPlatformResellerById(item.ownerPlatformResellerId);
                const statnusName = this.iccidStatusHelper.getName(item.mvnoStatus);

                csvList.push({
                    msisdn: item.msisdn,
                    partnerName: partner ? partner.mainname : '',
                    numberState: i18next.t(msisdnStatus(item.numberState)).toString(),
                    sharedDate: item.sharedDate ? DateHelperTs.format(item.sharedDate, 'YYYY-MM-DD') : '',
                    sharedEndDate: item.sharedEndDate ? DateHelperTs.format(item.sharedEndDate, 'YYYY-MM-DD') : '',
                    activationCount: item.activationCount,
                    portinDate: item.portinDate ? DateHelperTs.format(item.portinDate, 'YYYY-MM-DD') : '',
                    // indicatedRangeName: this.indicatedRangeLoader.getName(item.indicatedRangeId),
                });
            });
        }
        return csvList;
    };
}
